/**
 * №13.1 (§1.2) — the launched-verb form registry.
 *
 * `list_context_actions` (server) decides WHICH actions render on a record and
 * binds their prefill + lock. When a launched action is a first-party verb (not
 * synthesised entity CRUD), this ONE data table tells the generic VerbLaunchModal
 * which additional inputs to collect — so there is still no per-page prefill
 * wiring (the matter context always arrives locked from the registry). Synthesised
 * `create_<slug>` actions bypass this and open CreateEntityModal directly.
 *
 * A field whose `name` matches a locked prefill key becomes an editable, seeded
 * input (e.g. conflict-check party names); a locked key with no field renders as
 * a hard lock chip (e.g. matter_id).
 */

export type VerbFieldKind = "text" | "number" | "enum" | "date" | "entities" | "ledger" | "names" | "arrangement" | "resource";

export interface VerbField {
  name: string;
  label: string;
  kind: VerbFieldKind;
  required?: boolean;
  choices?: string[];
  /** №13.3 §2 — for kind "enum": derive the choices from a Tier-E type's field
   *  (its live, firm-configured option set) at render, instead of hardcoding an
   *  enum-value copy that can drift. Wins over a literal `choices`. */
  optionsFrom?: { type: string; field: string };
  /** For kind "entities": the entity type slug to pick from. */
  source?: string;
  placeholder?: string;
  /** Default seed value. */
  default?: string;
  /** For kind "arrangement": which arg the picked arrangement id maps to. */
  as?: string;
}

export interface VerbFormSpec {
  title?: string;
  submitLabel: string;
  intro?: string;
  fields: VerbField[];
  /** react-query key prefixes to invalidate on success. */
  invalidate?: string[][];
  successMessage?: (result: unknown) => string;
  /** №13.3 §1 — resolve the created record from the verb's result so the
   *  completion contract can route to it (open) or add a "View" link (stay).
   *  Return null when the verb produces no navigable record. */
  resultTarget?: (result: unknown) => { slug: string; id: string } | null;
}

/** №13.3 §1 — read `<obj>.<key>.id` (or `<obj>.<key>` when it's already an id)
 *  from a verb result, for resultTarget helpers. */
function idAt(result: unknown, key: string): string | null {
  if (!result || typeof result !== "object") return null;
  const v = (result as Record<string, unknown>)[key];
  if (typeof v === "string") return v;
  if (v && typeof v === "object" && typeof (v as { id?: unknown }).id === "string") return (v as { id: string }).id;
  return null;
}

export const VERB_FORMS: Record<string, VerbFormSpec> = {
  record_time: {
    title: "Record time",
    submitLabel: "Record time",
    fields: [
      { name: "fee_arrangement_id", label: "Arrangement", kind: "arrangement", as: "fee_arrangement_id" },
      { name: "resource", label: "Resource", kind: "resource" },
      { name: "work_type", label: "Work type", kind: "text", placeholder: "Optional" },
      { name: "narrative", label: "What did you do?", kind: "text" },
      { name: "units", label: "Units (hours)", kind: "number" },
      { name: "billable", label: "Billable", kind: "enum", choices: ["yes", "no"], default: "yes" },
    ],
    invalidate: [["commercial", "time-entries"], ["commercial", "wip"]],
    successMessage: () => "Time recorded",
    resultTarget: (r) => { const id = idAt(r, "time_entry"); return id ? { slug: "time-entry", id } : null; },
  },
  // №23 Stage C3 (D-MS3 rev hole 2) — the rate ladder's type-over verb. Same
  // raw-id-paste caveat as add_matter_team_member (no combined member+hire
  // picker yet). effective_from defaults server-side to today when left blank.
  set_matter_effective_rate: {
    title: "Set effective rate",
    submitLabel: "Set rate",
    intro: "Override the rate for one person on this matter. History-preserving: a mid-life change closes the current rate and opens a new one; a same-day change overwrites.",
    fields: [
      { name: "resource_kind", label: "Type", kind: "enum", required: true, default: "user", choices: ["user", "agent"] },
      { name: "resource_id", label: "Person or hire id", kind: "text", required: true, placeholder: "user id (Members page) or hire id (its node id)" },
      { name: "resource_label", label: "Display name", kind: "text", placeholder: "Optional — resolved automatically for a hire" },
      { name: "rate_minor", label: "Rate (minor units — e.g. cents)", kind: "number", required: true },
      { name: "effective_from", label: "Effective from", kind: "date", placeholder: "Optional — defaults to today" },
    ],
    invalidate: [["commercial", "effective-rates"]],
    successMessage: () => "Effective rate set",
  },
  create_matter_fee_arrangement: {
    title: "Add fee arrangement",
    submitLabel: "Add arrangement",
    fields: [
      { name: "description", label: "Label", kind: "text" },
      // №13.3 §2 — method choices derive from the fee-arrangement type's field.
      { name: "billing_method", label: "Method", kind: "enum", required: true, optionsFrom: { type: "fee-arrangement", field: "billing_method" } },
    ],
    invalidate: [["commercial", "fee-arrangements"]],
    successMessage: () => "Fee arrangement opened",
    resultTarget: (r) => { const id = idAt(r, "fee_arrangement"); return id ? { slug: "fee-arrangement", id } : null; },
  },
  apply_fee_package: {
    title: "Apply fee package",
    submitLabel: "Apply package",
    intro: "Creates a fee arrangement and copies the package's lines as a snapshot (later package edits never change it).",
    fields: [{ name: "fee_package_id", label: "Fee package", kind: "entities", source: "fee-package", required: true }],
    invalidate: [["commercial", "fee-arrangements"]],
    successMessage: () => "Fee package applied",
    resultTarget: (r) => { const id = idAt(r, "fee_arrangement_id"); return id ? { slug: "fee-arrangement", id } : null; },
  },
  prepare_invoice: {
    title: "Prepare invoice",
    submitLabel: "Prepare invoice",
    intro: "Assembles a draft invoice from this matter's unbilled WIP and posted fixed fee lines. Posting is approval-gated on the WIP worksheet.",
    fields: [
      { name: "fee_arrangement_id", label: "Scope to arrangement (optional)", kind: "arrangement", as: "fee_arrangement_id" },
      { name: "title", label: "Invoice title (optional)", kind: "text" },
    ],
    invalidate: [["commercial", "wip"], ["commercial", "draft-invoices"]],
    successMessage: () => "Draft invoice assembled — post it on the WIP worksheet",
    resultTarget: (r) => { const id = idAt(r, "draft_invoice_id"); return id ? { slug: "draft-invoice", id } : null; },
  },
  run_conflict_check: {
    title: "Run conflict check",
    submitLabel: "Run conflict check",
    intro: "Searches existing contacts and matters (all roles + the employment hop) for the party names and records the result as evidence (r 128 trail).",
    fields: [{ name: "names", label: "Party / subject names (semicolon-separated)", kind: "names", required: true }],
    invalidate: [["entities", "conflict-check"]],
    successMessage: () => "Conflict check recorded",
  },
  ensure_matter_trust_account: {
    title: "Link trust account",
    submitLabel: "Link trust account",
    intro: "Opens (idempotently) the client's trust account within the chosen ledger and joins it to this matter by owner.",
    fields: [{ name: "ledger_id", label: "Trust ledger", kind: "ledger", required: true }],
    invalidate: [["legal", "trust-balance"]],
    successMessage: () => "Trust account linked",
  },
  // №13.2 — add a party (the related-as join). Contact is picked from the one
  // Contacts master; role is the firm's party_role set (client is system-locked).
  // The first (or is_primary) client becomes the primary client automatically.
  add_matter_party: {
    title: "Add party",
    submitLabel: "Add party",
    intro: "Link a contact to this matter with a related-as role. A matter has one-or-many clients (role = client) plus any other roles.",
    fields: [
      { name: "contact_id", label: "Contact", kind: "entities", source: "contact", required: true },
      {
        name: "role",
        label: "Related as",
        kind: "enum",
        required: true,
        default: "client",
        // №13.3 §2 — the role choices ARE the firm's `party_role` option set,
        // derived from the matter-party type's `role` field at render — no
        // hardcoded copy of the 10 roles that could drift from the firm's set.
        optionsFrom: { type: "matter-party", field: "role" },
      },
    ],
    invalidate: [["matter-parties"]],
    successMessage: () => "Party added",
  },
  // №23 Stage C3 (D-MS4 rev) — the matter TEAM assignment (INTERNAL-facing,
  // vs add_matter_party's external-facing). subject_id is a raw id paste
  // (list_members for a user, the hire's node id for an agent) — a proper
  // combined member+hire PICKER is a follow-up UI item, not this dispatch's
  // scope (the coupled data-model work). matter_role's choices are hardcoded
  // (MATTER_ROLE_SEED, `src/modules/legal/definition.ts`) because `optionsFrom`
  // needs a Tier-E entity-type field to bind to and `matter_team` is a raw
  // primitive table, not an entity type — a firm that renames/adds a matter
  // role here will see this list drift until that seam is built.
  add_matter_team_member: {
    title: "Add team member",
    submitLabel: "Add to team",
    intro: "Assign a workspace member or a hire to this matter, with a matter role and an optional per-person rate override.",
    fields: [
      { name: "subject_kind", label: "Type", kind: "enum", required: true, default: "user", choices: ["user", "agent"] },
      { name: "subject_id", label: "Person or hire id", kind: "text", required: true, placeholder: "user id (Members page) or hire id (its node id)" },
      {
        name: "matter_role",
        label: "Matter role",
        kind: "enum",
        required: true,
        choices: ["responsible_solicitor", "assisting_solicitor", "paralegal", "file_clerk", "trainee", "reviewer", "other"],
      },
      { name: "rate_override_minor", label: "Rate override (minor units — e.g. cents)", kind: "number", placeholder: "Optional" },
    ],
    invalidate: [["legal", "matter-team"]],
    successMessage: () => "Team member added",
  },
};

/**
 * №13.1 (design-pack §3) — one source of truth for how a `conflict-check`
 * `result` renders. Shared by the VerbLaunchModal result surface (M16) and the
 * matter Conflicts-tab evidence rows (M19) so the badge/pill reads identically
 * everywhere. Maps the three result states to the `pill--*` classes.
 */
export function conflictResultDisplay(result: string | undefined | null): { label: string; pill: string } {
  switch (result) {
    case "hit":
      return { label: "HIT", pill: "pill--declined" };
    case "cleared_with_note":
      return { label: "CLEARED WITH NOTE", pill: "pill--pending" };
    case "clear":
      return { label: "CLEAR", pill: "pill--approved" };
    default:
      return { label: (result ?? "—").toUpperCase(), pill: "pill--draft" };
  }
}

/** Verbs that create a plain entity row (open CreateEntityModal, not VerbLaunchModal). */
export function synthesisedCreateSlug(verb: string): string | null {
  const m = /^create_([a-z0-9_]+)$/.exec(verb);
  if (!m) return null;
  // First-party create verbs that are NOT synthesised CRUD live in VERB_FORMS.
  if (VERB_FORMS[verb]) return null;
  return m[1]!.replace(/_/g, "-");
}
